// src/scenes/vehicles/components/VehiclesRegister.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  TextField,
  MenuItem,
  Stack,
  Snackbar,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Header from "../../../components/Header";
import { createVehicle } from "../../../services/vehicleService";
import { getDriversByBoss } from "../../../services/driverService";
import { getLicensesByBoss, getMyLicenses } from "../../../services/licenseService";

/* ===== Paletas coherentes con Turnos.jsx ===== */
const PALETTE_DARK = {
  cardBg: "#1E293B",
  cardBorder: "#334155",
  text: "#F1F5F9",
  label: "#94A3B8",
  title: "#E2E8F0",
  accent: "#38BDF8",
  btn: "#3B82F6",
  btnHover: "#2563EB",
};
const PALETTE_LIGHT = {
  cardBg: "#ECECEC",
  cardBorder: "#E5E7EB",
  text: "#1E293B",
  label: "#475569",
  title: "#111827",
  accent: "#38BDF8",
  btn: "#2563EB",
  btnHover: "#1D4ED8",
};

const VehiclesRegister = () => {
  const theme = useTheme();
  const palette = useMemo(
    () => (theme.palette.mode === "dark" ? PALETTE_DARK : PALETTE_LIGHT),
    [theme.palette.mode]
  );
  const navigate = useNavigate();

  // Redux
  const { userId, type: userType, driverId, driverName } = useSelector((state) => state.user);
  const [licenseOptions, setLicenseOptions] = useState([]);
  const hasLicenses = licenseOptions.length > 0;
  const defaultLicenseId = licenseOptions[0]?._id ?? "";
  const isDriverUser = userType === "Driver";

  // Form state
  const [vehicleModel, setVehicleModel] = useState("");
  const [vehicleBrand, setVehicleBrand] = useState("");
  const [year, setYear] = useState("");
  const [kilometres, setKilometres] = useState("");
  const [licensePlate, setLicensePlate] = useState("");
  const [licenseId, setLicenseId] = useState(defaultLicenseId);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState(driverId || "");

  // UX state
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  // ===== Estilo de inputs con borde visible =====
  const inputSx = {
    flexGrow: 1,
    "& .MuiInputLabel-root": { color: palette.label },
    "& .MuiInputLabel-root.Mui-focused": { color: palette.text },
    "& .MuiInputBase-input": { color: palette.text },
    "& .MuiInputBase-input.Mui-focused": { color: palette.text },
    "& .MuiOutlinedInput-root": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: palette.cardBorder,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: palette.label,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: palette.accent,
        borderWidth: 2,
      },
    },
  };

  useEffect(() => {
    const loadLicenses = async () => {
      try {
        let data = [];
        if (userType === "Boss") {
          data = await getLicensesByBoss();
        } else {
          data = await getMyLicenses();
        }
        setLicenseOptions(data || []);
        if (!licenseId && data?.length === 1) {
          setLicenseId(data[0]._id);
        }
      } catch (err) {
        setLicenseOptions([]);
        setToast({
          type: "error",
          msg: err?.message || "No se pudieron cargar las licencias.",
        });
      }
    };

    loadLicenses();
  }, [licenseId, userType]);

  useEffect(() => {
    const loadDrivers = async () => {
      if (isDriverUser) {
        setDrivers(
          driverId
            ? [{ _id: driverId, name: driverName || "Conductor actual", lastName: "" }]
            : []
        );
        setSelectedDriverId(driverId || "");
        return;
      }
      try {
        const list = await getDriversByBoss();
        setDrivers(list || []);
        if (!selectedDriverId && list?.length === 1) {
          setSelectedDriverId(list[0]._id);
        }
      } catch (err) {
        setToast({
          type: "error",
          msg: err?.message || "No se pudieron cargar los conductores.",
        });
      }
    };

    loadDrivers();
  }, [driverId, driverName, isDriverUser, selectedDriverId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId) return setToast({ type: "error", msg: "No se ha encontrado el usuario en sesión." });
    if (!licenseId) return setToast({ type: "error", msg: "Selecciona una licencia." });
    if (!vehicleModel.trim()) return setToast({ type: "error", msg: "El modelo es obligatorio." });
    if (!selectedDriverId) return setToast({ type: "error", msg: "Selecciona un conductor." });

    const parsedYear = year ? parseInt(year, 10) : undefined;
    const parsedKm = kilometres ? parseInt(kilometres, 10) : undefined;

    if (parsedYear && (parsedYear < 1970 || parsedYear > new Date().getFullYear() + 1)) {
      return setToast({ type: "error", msg: "Año no válido." });
    }
    if (parsedKm && parsedKm < 0) {
      return setToast({ type: "error", msg: "Los kilómetros no pueden ser negativos." });
    }

    const newVehicle = {
      user: userId,
      model: vehicleModel.trim(),
      brand: vehicleBrand.trim() || undefined,
      year: parsedYear,
      kilometres: parsedKm,
      licensePlate: licensePlate.trim().toUpperCase() || undefined,
      license: licenseId,
      driver: selectedDriverId,
    };

    try {
      setLoading(true);
      await createVehicle(newVehicle);
      setToast({ type: "success", msg: "Vehículo creado correctamente." });
      navigate("/vehicles");
    } catch (error) {
      setToast({ type: "error", msg: error?.message || "Error al crear vehículo." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Header arriba */}
      <Box sx={{ m: "20px" }}>
        <Header title="Vehículos" subtitle="Registrar un nuevo vehículo" />
      </Box>

      {/* Contenedor de la card */}
      <Box
        sx={{
          minHeight: "calc(100vh - 220px)",
          display: "grid",
          placeItems: "start center",
          px: 2,
        }}
      >
        <Box
          sx={{
            backgroundColor: palette.cardBg,
            border: `1px solid ${palette.cardBorder}`,
            borderRadius: "14px",
            p: 3,
            boxShadow:
              palette === PALETTE_LIGHT
                ? "0 8px 22px rgba(2,6,23,0.08)"
                : "0 3px 10px rgba(0,0,0,0.24)",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
            maxWidth: "1200px",
            mx: "auto",
          }}
        >
          {/* Cabecera de la card */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            gap={1.25}
          >
            <Box>
              <Typography variant="h6" sx={{ color: palette.title, fontWeight: 800 }}>
                Registro de vehículo
              </Typography>
              <Typography variant="body2" sx={{ color: palette.label }}>
                Añade los datos del vehículo para tu licencia de taxi.
              </Typography>
            </Box>

            <Stack direction="row" gap={1}>
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                sx={{
                  borderColor: palette.cardBorder,
                  color: palette.text,
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": { borderColor: palette.label },
                }}
              >
                Volver
              </Button>
            </Stack>
          </Stack>

          {/* Formulario */}
          <Box
            component="form"
            id="vehicle-form"
            onSubmit={handleSubmit}
            sx={{
              backgroundColor: "transparent",
              border: `1px dashed ${palette.cardBorder}`,
              borderRadius: "12px",
              p: 3,
              width: "100%",
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={6} lg={4} sx={{ display: "flex" }}>
                <TextField
                  select
                  fullWidth
                  label="Conductor"
                  value={selectedDriverId}
                  onChange={(e) => setSelectedDriverId(e.target.value)}
                  required
                  helperText={
                    isDriverUser
                      ? "El vehículo se asociará a tu perfil."
                      : drivers.length
                      ? "Selecciona el conductor responsable del vehículo."
                      : "Añade un conductor antes de registrar el vehículo."
                  }
                  disabled={isDriverUser || drivers.length === 0}
                  sx={inputSx}
                >
                  {drivers.map((d) => (
                    <MenuItem key={d._id} value={d._id}>
                      {[d.name, d.lastName].filter(Boolean).join(" ") || d._id}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6} md={6} lg={4} sx={{ display: "flex" }}>
                <TextField
                  select
                  fullWidth
                  label="Licencia"
                  value={licenseId}
                  onChange={(e) => setLicenseId(e.target.value)}
                  required
                  helperText={
                    hasLicenses
                      ? "Selecciona la licencia a la que pertenece el vehículo"
                      : "Necesitas crear una licencia para asociar el vehículo."
                  }
                  disabled={!hasLicenses}
                  sx={inputSx}
                >
                  {licenseOptions.map((l) => (
                    <MenuItem key={l._id} value={l._id}>
                      {l.name || l.number || l.licenseNumber || l._id}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6} md={6} lg={4} sx={{ display: "flex" }}>
                <TextField
                  fullWidth
                  label="Modelo"
                  value={vehicleModel}
                  onChange={(e) => setVehicleModel(e.target.value)}
                  required
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6} lg={4} sx={{ display: "flex" }}>
                <TextField
                  fullWidth
                  label="Marca"
                  value={vehicleBrand}
                  onChange={(e) => setVehicleBrand(e.target.value)}
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3} lg={3} sx={{ display: "flex" }}>
                <TextField
                  fullWidth
                  label="Año"
                  type="number"
                  inputProps={{ min: 1970, max: new Date().getFullYear() + 1 }}
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3} lg={3} sx={{ display: "flex" }}>
                <TextField
                  fullWidth
                  label="Kilómetros"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={kilometres}
                  onChange={(e) => setKilometres(e.target.value)}
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={6} sx={{ display: "flex" }}>
                <TextField
                  fullWidth
                  label="Matrícula"
                  value={licensePlate}
                  onChange={(e) => setLicensePlate(e.target.value.toUpperCase())}
                  placeholder="1234-ABC"
                  inputProps={{ maxLength: 10 }}
                  helperText="Se guarda en mayúsculas."
                  sx={inputSx}
                />
              </Grid>
            </Grid>

            {/* Botonera inferior */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              gap={1.25}
              justifyContent="flex-end"
              alignItems="center"
              mt={3}
            >
              <Button
                variant="outlined"
                onClick={() => navigate("/vehicles")}
                disabled={loading}
                sx={{
                  borderColor: palette.cardBorder,
                  color: palette.text,
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": { borderColor: palette.label },
                }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                  backgroundColor: palette.btn,
                  color: "white",
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": { backgroundColor: palette.btnHover },
                }}
              >
                {loading ? "Guardando..." : "Guardar vehículo"}
              </Button>
            </Stack>
          </Box>
        </Box>

        {/* Toast */}
        <Snackbar
          open={!!toast}
          autoHideDuration={3500}
          onClose={() => setToast(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          {toast ? (
            <Alert onClose={() => setToast(null)} severity={toast.type} variant="filled" sx={{ width: "100%" }}>
              {toast.msg}
            </Alert>
          ) : null}
        </Snackbar>
      </Box>
    </>
  );
};

export default VehiclesRegister;
